import type { EmployeeOption, Issue, PreviewRun, Statement } from './types';

/**
 * SYNTHETIC, clearly fictitious dataset behind MockPayrollHistoryImportGateway ("Example Manufacturing
 * LLC"): no real names, addresses, SSNs or bank data, and the percentages below are arbitrary placeholders
 * for demo arithmetic — NOT tax guidance. Everything is built from LINE-level data; statement, run and
 * package totals are COMPUTED from the lines (in integer cents) so the numbers always reconcile exactly:
 *   net pay = gross + reimbursements - employee taxes - pre-tax deductions - post-tax deductions.
 */

// ---- money helpers (integer cents; avoids float drift) ----
const cents = (amount: number): number => Math.round(amount * 100);
const fromCents = (c: number): number => c / 100;
export const sumMoney = (values: number[]): number => fromCents(values.reduce((acc, v) => acc + cents(v), 0));
const pct = (amount: number, rate: number): number => fromCents(Math.round(cents(amount) * rate));

// ---- employees ----
export interface SourceEmployee {
  sourceEmployeeId: string;
  employeeNumber: string;
  displayName: string;
  email: string;
  employmentStatus: string;
  /** The EPN employee this employee is matched to automatically, or null when user confirmation is needed. */
  autoMatch: { epnEmployeeId: string; method: 'StoredMapping' | 'ExactExternalId' } | null;
  /** Email-match suggestion offered (never auto-applied) when there is no automatic match. */
  suggestionEpnEmployeeId: string | null;
}

export const MOCK_COMPANY_NAME = 'Example Manufacturing LLC';
export const MOCK_SOURCE_COMPANY_REFERENCE = 'EXAMPLE-MFG-001';

export const SOURCE_EMPLOYEES: SourceEmployee[] = [
  { sourceEmployeeId: 'SRC-E-001', employeeNumber: 'E-001', displayName: 'Avery Example', email: 'avery.example@example.test', employmentStatus: 'Active', autoMatch: { epnEmployeeId: 'epn-emp-1001', method: 'ExactExternalId' }, suggestionEpnEmployeeId: null },
  { sourceEmployeeId: 'SRC-E-002', employeeNumber: 'E-002', displayName: 'Blake Sample', email: 'blake.sample@example.test', employmentStatus: 'Active', autoMatch: { epnEmployeeId: 'epn-emp-1002', method: 'ExactExternalId' }, suggestionEpnEmployeeId: null },
  { sourceEmployeeId: 'SRC-E-003', employeeNumber: 'E-003', displayName: 'Casey Placeholder', email: 'casey.placeholder@example.test', employmentStatus: 'Active', autoMatch: { epnEmployeeId: 'epn-emp-1003', method: 'StoredMapping' }, suggestionEpnEmployeeId: null },
  { sourceEmployeeId: 'SRC-E-004', employeeNumber: 'E-004', displayName: 'Drew Fictional', email: 'drew.fictional@example.test', employmentStatus: 'Active', autoMatch: { epnEmployeeId: 'epn-emp-1004', method: 'ExactExternalId' }, suggestionEpnEmployeeId: null },
  { sourceEmployeeId: 'SRC-E-005', employeeNumber: 'E-005', displayName: 'Emery Demo', email: 'emery.demo@example.test', employmentStatus: 'Active', autoMatch: { epnEmployeeId: 'epn-emp-1005', method: 'ExactExternalId' }, suggestionEpnEmployeeId: null },
  // Inactive historical employee that still matches an (inactive) EPN employee automatically.
  { sourceEmployeeId: 'SRC-E-006', employeeNumber: 'E-006', displayName: 'Finley Historic', email: 'finley.historic@example.test', employmentStatus: 'Terminated', autoMatch: { epnEmployeeId: 'epn-emp-1006', method: 'ExactExternalId' }, suggestionEpnEmployeeId: null },
  // Needs a user-confirmed mapping; an email match is suggested but never applied automatically.
  { sourceEmployeeId: 'SRC-E-007', employeeNumber: 'E-007', displayName: 'Gray Unmatched', email: 'gray.unmatched@example.test', employmentStatus: 'Active', autoMatch: null, suggestionEpnEmployeeId: 'epn-emp-2007' },
];

/** The (mock) EPN employee directory the mapping picker searches. */
export const EPN_EMPLOYEES: EmployeeOption[] = [
  { epnEmployeeId: 'epn-emp-1001', employeeNumber: 'EMP-1001', displayName: 'Avery Example', isActive: true },
  { epnEmployeeId: 'epn-emp-1002', employeeNumber: 'EMP-1002', displayName: 'Blake Sample', isActive: true },
  { epnEmployeeId: 'epn-emp-1003', employeeNumber: 'EMP-1003', displayName: 'Casey Placeholder', isActive: true },
  { epnEmployeeId: 'epn-emp-1004', employeeNumber: 'EMP-1004', displayName: 'Drew Fictional', isActive: true },
  { epnEmployeeId: 'epn-emp-1005', employeeNumber: 'EMP-1005', displayName: 'Emery Demo', isActive: true },
  { epnEmployeeId: 'epn-emp-1006', employeeNumber: 'EMP-1006', displayName: 'Finley Historic', isActive: false },
  { epnEmployeeId: 'epn-emp-2007', employeeNumber: 'EMP-2007', displayName: 'Gray Unmatched', isActive: true },
  { epnEmployeeId: 'epn-emp-2008', employeeNumber: 'EMP-2008', displayName: 'Harper Spare', isActive: true },
  { epnEmployeeId: 'epn-emp-2009', employeeNumber: 'EMP-2009', displayName: 'Indy Spare', isActive: true },
  // Fictional people used by the guided-mapping demo register (a sample CSV in the tests and walkthroughs).
  { epnEmployeeId: 'epn-emp-3001', employeeNumber: 'EMP-3001', displayName: 'Taylor Morgan', isActive: true },
  { epnEmployeeId: 'epn-emp-3002', employeeNumber: 'EMP-3002', displayName: 'Jordan Rivera', isActive: true },
];

// ---- statement construction from lines ----
interface StatementSpec {
  id: string;
  employeeId: string;
  status: Statement['statementStatus'];
  corrects?: string;
  regularHours: number;
  rate: number;
  overtimeHours?: number;
  bonus?: number;
  unmappedEarning?: number;
  reimbursement?: number;
  /** Adds an employee-scope local tax line whose jurisdiction display name is not recognized. */
  unrecognizedJurisdiction?: boolean;
  postTax?: number;
}

const FED = 'Federal';
const STATE = 'Fictional State';
export const UNRECOGNIZED_JURISDICTION = 'Fictional Borough (local)';
export const UNMAPPED_EARNING_CODE = 'XTRA-LEGACY';

function buildStatement(spec: StatementSpec, epnIdFor: (sourceEmployeeId: string) => string | null): Statement {
  const emp = SOURCE_EMPLOYEES.find((e) => e.sourceEmployeeId === spec.employeeId);
  if (!emp) throw new Error(`Unknown mock employee ${spec.employeeId}`);
  const line = (suffix: string) => `${spec.id}-${suffix}`;

  const earnings: Statement['earnings'] = [
    { sourceLineId: line('E1'), code: 'REG', category: 'Regular', hours: spec.regularHours, rate: spec.rate, amount: fromCents(Math.round(spec.regularHours * cents(spec.rate))), isTaxable: true },
  ];
  if (spec.overtimeHours) {
    const otRate = fromCents(Math.round(cents(spec.rate) * 1.5));
    earnings.push({ sourceLineId: line('E2'), code: 'OT', category: 'Overtime', hours: spec.overtimeHours, rate: otRate, amount: fromCents(Math.round(spec.overtimeHours * cents(otRate))), isTaxable: true });
  }
  if (spec.bonus) earnings.push({ sourceLineId: line('E3'), code: 'BONUS', category: 'Bonus', hours: null, rate: null, amount: spec.bonus, isTaxable: true });
  if (spec.unmappedEarning) earnings.push({ sourceLineId: line('E4'), code: UNMAPPED_EARNING_CODE, category: null, hours: null, rate: null, amount: spec.unmappedEarning, isTaxable: true });

  const gross = sumMoney(earnings.map((e) => e.amount));

  const deductions: Statement['deductions'] = [
    { sourceLineId: line('D1'), code: '401K', classification: 'PreTax', employeeAmount: pct(gross, 0.04), employerAmount: pct(gross, 0.02) },
    { sourceLineId: line('D2'), code: 'HEALTH', classification: 'PreTax', employeeAmount: 50, employerAmount: 120 },
  ];
  if (spec.postTax) deductions.push({ sourceLineId: line('D3'), code: 'UNION', classification: 'PostTax', employeeAmount: spec.postTax, employerAmount: 0 });

  const preTax = sumMoney(deductions.filter((d) => d.classification === 'PreTax').map((d) => d.employeeAmount));
  const postTax = sumMoney(deductions.filter((d) => d.classification === 'PostTax').map((d) => d.employeeAmount));
  const taxable = sumMoney([gross, -preTax]);

  const taxes: Statement['taxes'] = [
    { sourceLineId: line('T1'), name: 'Federal Income Tax', jurisdiction: FED, scope: 'Employee', taxableWageBase: taxable, amount: pct(taxable, 0.1) },
    { sourceLineId: line('T2'), name: 'Social Security', jurisdiction: FED, scope: 'Employee', taxableWageBase: taxable, amount: pct(taxable, 0.062) },
    { sourceLineId: line('T3'), name: 'Medicare', jurisdiction: FED, scope: 'Employee', taxableWageBase: taxable, amount: pct(taxable, 0.0145) },
    { sourceLineId: line('T4'), name: 'State Income Tax', jurisdiction: STATE, scope: 'Employee', taxableWageBase: taxable, amount: pct(taxable, 0.03) },
    { sourceLineId: line('T5'), name: 'Social Security (employer)', jurisdiction: FED, scope: 'Employer', taxableWageBase: taxable, amount: pct(taxable, 0.062) },
    { sourceLineId: line('T6'), name: 'Medicare (employer)', jurisdiction: FED, scope: 'Employer', taxableWageBase: taxable, amount: pct(taxable, 0.0145) },
  ];
  if (spec.unrecognizedJurisdiction) {
    taxes.push({ sourceLineId: line('T7'), name: 'Local Services Tax', jurisdiction: UNRECOGNIZED_JURISDICTION, scope: 'Employee', taxableWageBase: taxable, amount: 5 });
  }

  const employeeTaxes = sumMoney(taxes.filter((t) => t.scope === 'Employee').map((t) => t.amount));
  const employerTaxes = sumMoney(taxes.filter((t) => t.scope === 'Employer').map((t) => t.amount));
  const reimbursements = spec.reimbursement ?? 0;
  const net = fromCents(cents(gross) + cents(reimbursements) - cents(employeeTaxes) - cents(preTax) - cents(postTax));

  return {
    sourcePayStatementId: spec.id,
    sourceEmployeeId: spec.employeeId,
    employeeDisplayName: emp.displayName,
    epnEmployeeId: epnIdFor(spec.employeeId),
    statementStatus: spec.status,
    correctsSourcePayStatementId: spec.corrects ?? null,
    grossPay: gross,
    taxableWages: taxable,
    preTaxDeductions: preTax,
    postTaxDeductions: postTax,
    employeeTaxes,
    employerTaxes,
    reimbursements,
    netPay: net,
    paymentMethodDescription: 'Direct deposit (historical record)',
    // Masked at the source; synthetic last-4 derived from the employee id so it is stable and fictitious.
    paymentReference: `****${emp.sourceEmployeeId.slice(-3).padStart(4, '0')}`,
    earnings,
    deductions,
    taxes,
  };
}

interface RunSpec {
  sourcePayrollRunId: string;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  statements: StatementSpec[];
}

const std = (id: string, employeeId: string, hours = 80, rate = 25): StatementSpec => ({ id, employeeId, status: 'Original', regularHours: hours, rate });

const RUN_SPECS: RunSpec[] = [
  {
    sourcePayrollRunId: 'RUN-2025-001', periodStart: '2025-01-01', periodEnd: '2025-01-14', payDate: '2025-01-17',
    statements: [
      std('PS-1-001', 'SRC-E-001', 80, 30), std('PS-1-002', 'SRC-E-002', 80, 28), std('PS-1-003', 'SRC-E-003', 76, 24),
      std('PS-1-004', 'SRC-E-004', 80, 26), std('PS-1-005', 'SRC-E-005', 72, 22), std('PS-1-006', 'SRC-E-006', 80, 27),
    ],
  },
  {
    sourcePayrollRunId: 'RUN-2025-002', periodStart: '2025-01-15', periodEnd: '2025-01-28', payDate: '2025-01-31',
    statements: [
      std('PS-2-001', 'SRC-E-001', 80, 30), std('PS-2-002', 'SRC-E-002', 80, 28), std('PS-2-003', 'SRC-E-003', 80, 24),
      std('PS-2-004', 'SRC-E-004', 80, 26), std('PS-2-005', 'SRC-E-005', 80, 22), std('PS-2-006', 'SRC-E-006', 40, 27),
      { ...std('PS-2-007', 'SRC-E-007', 80, 31), postTax: 15 },
    ],
  },
  {
    sourcePayrollRunId: 'RUN-2025-003', periodStart: '2025-01-29', periodEnd: '2025-02-11', payDate: '2025-02-14',
    statements: [
      { ...std('PS-3-001', 'SRC-E-001', 80, 30), overtimeHours: 4 }, std('PS-3-002', 'SRC-E-002', 80, 28), std('PS-3-003', 'SRC-E-003', 80, 24),
      // The original statement for Drew was voided and re-issued as a correction in the same run.
      { ...std('PS-3-004', 'SRC-E-004', 80, 26), status: 'Voided' },
      { ...std('PS-3-004C', 'SRC-E-004', 80, 26), status: 'Corrected', corrects: 'PS-3-004', overtimeHours: 2 },
      std('PS-3-005', 'SRC-E-005', 80, 22), { ...std('PS-3-007', 'SRC-E-007', 80, 31), postTax: 15, unrecognizedJurisdiction: true },
    ],
  },
  {
    sourcePayrollRunId: 'RUN-2025-004', periodStart: '2025-02-12', periodEnd: '2025-02-25', payDate: '2025-02-28',
    statements: [
      std('PS-4-001', 'SRC-E-001', 80, 30), { ...std('PS-4-002', 'SRC-E-002', 80, 28), bonus: 500 },
      { ...std('PS-4-003', 'SRC-E-003', 80, 24), reimbursement: 25 }, std('PS-4-004', 'SRC-E-004', 80, 26),
      { ...std('PS-4-005', 'SRC-E-005', 80, 22), unmappedEarning: 40 }, { ...std('PS-4-007', 'SRC-E-007', 80, 31), postTax: 15 },
    ],
  },
];

export interface MockRunData {
  run: PreviewRun;
  statements: Statement[];
}

/** Builds the four runs. `epnIdFor` resolves each source employee's current EPN mapping (null when unmapped). */
export function buildMockRuns(epnIdFor: (sourceEmployeeId: string) => string | null): MockRunData[] {
  return RUN_SPECS.map((spec) => {
    const statements = spec.statements.map((s) => buildStatement(s, epnIdFor));
    // Voided statements stay visible but are excluded from every run total.
    const counted = statements.filter((s) => s.statementStatus !== 'Voided');
    const run: PreviewRun = {
      sourcePayrollRunId: spec.sourcePayrollRunId,
      periodStart: spec.periodStart,
      periodEnd: spec.periodEnd,
      payDate: spec.payDate,
      payrollType: 'Regular',
      runStatus: 'Completed',
      employeeCount: new Set(counted.map((s) => s.sourceEmployeeId)).size,
      grossPay: sumMoney(counted.map((s) => s.grossPay)),
      employeeTaxes: sumMoney(counted.map((s) => s.employeeTaxes)),
      employeeDeductions: sumMoney(counted.map((s) => s.preTaxDeductions + s.postTaxDeductions)),
      employerTaxes: sumMoney(counted.map((s) => s.employerTaxes)),
      netPay: sumMoney(counted.map((s) => s.netPay)),
      voidedStatementCount: statements.filter((s) => s.statementStatus === 'Voided').length,
      correctedStatementCount: statements.filter((s) => s.statementStatus === 'Corrected').length,
      alreadyImported: false,
    };
    return { run, statements };
  });
}

export const MOCK_EARNING_CODES = ['BONUS', 'OT', 'REG', UNMAPPED_EARNING_CODE];
export const MOCK_DEDUCTION_CODES = ['401K', 'HEALTH', 'UNION'];
export const MOCK_TAX_JURISDICTIONS = [FED, STATE, UNRECOGNIZED_JURISDICTION];

/** Non-mapping issues of the valid synthetic package: warnings and information only. */
export const VALID_PACKAGE_ISSUES: Issue[] = [
  { severity: 'Warning', code: 'EARNING_CODE_UNMAPPED', message: `Earning code ${UNMAPPED_EARNING_CODE} has no matching Escochex earning type. It is kept as imported text and shown as written in the source.`, file: 'pay_statement_earnings.csv', rowNumber: 31, sourceRecordId: 'PS-4-005-E4' },
  { severity: 'Warning', code: 'TAX_JURISDICTION_UNRECOGNIZED', message: `Tax jurisdiction "${UNRECOGNIZED_JURISDICTION}" is not recognized. Its display name is kept as written in the source.`, file: 'pay_statement_taxes.csv', rowNumber: 44, sourceRecordId: 'PS-3-007-T7' },
  { severity: 'Warning', code: 'VOIDED_STATEMENT_PRESENT', message: 'Pay statement PS-3-004 is voided and was re-issued as PS-3-004C. The voided statement is kept for reference and excluded from totals.', file: 'pay_statements.csv', rowNumber: 18, sourceRecordId: 'PS-3-004' },
  { severity: 'Information', code: 'INACTIVE_EMPLOYEE_MATCHED', message: 'Employee SRC-E-006 (Terminated) was matched to an inactive Escochex employee. Their history is imported as read-only records.', file: 'employees.csv', rowNumber: 7, sourceRecordId: 'SRC-E-006' },
];

export const COMMIT_REVALIDATION_REASON =
  'Validation failed when the import was submitted: a pay statement no longer reconciles. Nothing was imported. Cancel this import and upload a corrected package.';
export const COMMIT_ROLLBACK_REASON =
  'The import could not be completed and was rolled back. Nothing was imported. You can start a new import.';
export const COMMIT_REVALIDATION_ISSUE: Issue = {
  severity: 'BlockingError', code: 'UNBALANCED_PAY_STATEMENT',
  message: 'Pay statement PS-4-003 no longer reconciles at import time: net pay differs from gross + reimbursements - employee taxes - deductions.',
  file: 'pay_statements.csv', rowNumber: 27, sourceRecordId: 'PS-4-003',
};

export const MAPPING_MISSING_ISSUE: Issue = {
  severity: 'BlockingError', code: 'EMPLOYEE_MAPPING_MISSING',
  message: 'Employee SRC-E-007 (Gray Unmatched) is not matched to an Escochex employee. Confirm a mapping before continuing.',
  file: 'employees.csv', rowNumber: 8, sourceRecordId: 'SRC-E-007',
};

/** Blocking errors of the "invalid" scenario (file name contains "invalid"). */
export const INVALID_PACKAGE_ERRORS: Issue[] = [
  { severity: 'BlockingError', code: 'DUPLICATE_SOURCE_ID', message: 'Pay statement id PS-2-003 appears more than once in the package.', file: 'pay_statements.csv', rowNumber: 14, sourceRecordId: 'PS-2-003' },
  { severity: 'BlockingError', code: 'UNBALANCED_PAY_STATEMENT', message: 'Pay statement PS-3-002 does not balance: net pay 1,234.56 differs from gross + reimbursements - employee taxes - deductions (1,201.00).', file: 'pay_statements.csv', rowNumber: 22, sourceRecordId: 'PS-3-002' },
  { severity: 'BlockingError', code: 'CHECKSUM_MISMATCH', message: 'The checksum of pay_statement_taxes.csv does not match manifest.json. The file may have been changed after export.', file: 'manifest.json', rowNumber: null, sourceRecordId: 'pay_statement_taxes.csv' },
];

export function issuesToCsv(issues: Issue[]): string {
  const esc = (v: string | number | null): string => {
    const s = v === null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = issues.map((i) => [i.severity, i.code, i.message, i.file, i.rowNumber, i.sourceRecordId].map(esc).join(','));
  return ['severity,code,message,file,rowNumber,sourceRecordId', ...rows].join('\n');
}

/** Deterministic 64-hex stand-in for a content hash (mock only — equal name+size means "same package"). */
export function mockPackageHash(text: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  let out = '';
  for (let round = 0; round < 8; round++) {
    for (let i = 0; i < text.length; i++) {
      h1 = Math.imul(h1 ^ text.charCodeAt(i), 16777619) >>> 0;
      h2 = (Math.imul(h2 + text.charCodeAt(i), 2246822519) ^ h1) >>> 0;
    }
    out += (h1 ^ (h2 + round)).toString(16).padStart(8, '0').slice(-8);
  }
  return out;
}
